import Loading from '@platform-ui-common/components/Loading/index.js';
import usePartner from '@platform-ui-common/hooks/contexts/usePartner.js';
import apolloClient from '@platform-ui/apolloClient.js';
import { hasFullProfile } from '@platform-ui/utilities/auth/hasFullProfile.js';
import { usePostHog } from 'posthog-js/react';
import { ReactNode, useCallback, useContext, useEffect, useMemo } from 'react';
import { AuthenticationContext } from './context.js';
import { AuthState } from './types.js';
import { useIdentity } from './useIdentity.js';

export function AuthProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const identity = useIdentity();
  const partner = usePartner();
  const posthog = usePostHog();

  const user = useMemo(
    () => ({ gcip: identity.auth?.currentUser, core: identity?.coreUser }),
    [identity.auth?.currentUser, identity?.coreUser],
  );

  const resetAuthState = useCallback(async () => {
    await identity?.auth.signOut();
    apolloClient.clearStore();
    localStorage.clear();
    posthog.reset();
  }, [identity?.auth?.signOut, posthog?.reset]);

  useEffect(() => {
    if (partner?.id && user?.core?.partner?.id) {
      const differentPartner = partner.id !== user.core.partner.id;
      const isAdmin = !!user?.core?.admin?.id;
      if (differentPartner || isAdmin) resetAuthState();
    }
  });

  const value = useMemo(
    () => ({
      identity,
      loading: identity.loading,
      hasFullProfile: hasFullProfile(user?.core),
      resetAuthState,
      user,
    }),
    [identity, user, resetAuthState],
  );
  return (
    <AuthenticationContext.Provider value={value}>
      {identity.loading ? <Loading size="XXL" fullPage /> : children}
    </AuthenticationContext.Provider>
  );
}

export default function useAuth(): AuthState {
  return useContext(AuthenticationContext);
}
